import LoadingPaper from '@components/LoadingPaper';
import useIsGroupAdmin from '@hooks/useIsGroupAdmin';
import { updateAccessPin } from '@modules/api';
import { userMessage } from '@modules/api-error';
import { logError } from '@modules/logging';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import SaveOutlined from '@mui/icons-material/SaveOutlined';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useInterviewOptions } from '@providers/InterviewsProvider';
import { getNameFromId } from '@synergetx/puppet-interview-types';
import { useState } from 'react';
import { toast } from 'react-toastify';

// Allow group admins to set an access PIN for a group, or edit it if it already exists. They can also remove the PIN requirement if they want.
export default function AccessPIN() {
    const { selectedInterview } = useInterviewOptions();
    const groupId = selectedInterview?.groupId;
    const isGroupAdmin = useIsGroupAdmin();
    const [pin, setPin] = useState<string | null>(selectedInterview?.accessPIN ?? null);
    const [loading, setLoading] = useState<boolean>(false);

    // Sync state from props during render (React recommended pattern)
    const [prevAccessPIN, setPrevAccessPIN] = useState(selectedInterview?.accessPIN);
    if (selectedInterview?.accessPIN !== prevAccessPIN) {
        setPrevAccessPIN(selectedInterview?.accessPIN);
        setPin(selectedInterview?.accessPIN ?? null);
    }

    // Await the write before clearing `loading`, or both buttons re-enable mid-flight.
    const savePin = async (nextPin: string | null, verb: 'saving' | 'removing') => {
        if (!groupId) return toast.error('No group selected');

        setLoading(true);
        try {
            const { group } = await toast.promise(updateAccessPin(groupId, nextPin), {
                error: {
                    render: ({ data }) => userMessage(data, `Error ${verb} PIN`),
                },
                pending: verb === 'saving' ? 'Saving PIN...' : 'Removing PIN...',
                success: verb === 'saving' ? 'PIN saved' : 'PIN removed',
            });

            if (group) setPin(group.accessPIN ?? null);
        } catch (error) {
            logError(error as Error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = () => savePin(pin, 'saving');

    const handleRemove = () => savePin(null, 'removing');

    return (
        <LoadingPaper
            style={{
                width: '85vw',
                minHeight: '95vh',
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Grid
                container
                spacing={4}
                sx={{
                    maxWidth: 500,
                }}
            >
                <Grid size={12}>
                    <Alert severity="info">
                        <AlertTitle>
                            Access PIN <i>(Optional)</i>
                        </AlertTitle>
                        {isGroupAdmin
                            ? `Set an access PIN for this entire '${groupId ? getNameFromId(groupId) : ''}' project. Users will need to enter this PIN in the mobile app to access the survey.`
                            : 'This study requires an access PIN to access the survey in the mobile app.'}
                    </Alert>
                </Grid>

                <Grid size={12}>
                    <TextField
                        disabled={!isGroupAdmin}
                        fullWidth
                        placeholder="none"
                        id="access-pin"
                        label="Access PIN"
                        type="text"
                        variant="outlined"
                        value={pin || ''}
                        onChange={(e) => setPin(e.target.value)}
                    />
                </Grid>
                {isGroupAdmin && (
                    <Grid size={12} spacing={2}>
                        <Stack direction="row" spacing={3}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSave}
                                disabled={loading}
                                startIcon={<SaveOutlined />}
                                sx={{ paddingX: 4 }}
                            >
                                Save
                            </Button>
                            <Button
                                color="error"
                                onClick={handleRemove}
                                disabled={loading || !pin}
                                startIcon={<DeleteOutlined />}
                                variant="outlined"
                            >
                                Remove
                            </Button>
                        </Stack>
                    </Grid>
                )}
            </Grid>
        </LoadingPaper>
    );
}
